import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook to detect online/offline status with real internet connectivity checking
 * Optimized for slow 3G connections to minimize false offline alerts
 * @returns {boolean} - Returns true if online, false if offline
 */
export const useOnlineStatus = () => {
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const checkInProgressRef = useRef(false);
    const lastCheckTimeRef = useRef(0);
    const consecutiveFailuresRef = useRef(0);

    // Function to check actual internet connectivity
    const checkOnlineStatus = async () => {
        // Prevent multiple simultaneous checks
        if (checkInProgressRef.current) {
            return;
        }

        checkInProgressRef.current = true;
        const now = Date.now();
        lastCheckTimeRef.current = now;

        // First, check navigator.onLine - if false, we're definitely offline
        if (!navigator.onLine) {
            console.log('🔴 navigator.onLine is false - OFFLINE');
            consecutiveFailuresRef.current++;
            if (consecutiveFailuresRef.current >= 4) { // Increased from 3 to 4 for slow connections
                setIsOnline(false);
            }
            checkInProgressRef.current = false;
            return false;
        }

        try {
            // Check actual internet connectivity by trying to reach an external reliable resource
            const controller = new AbortController();
            const timeoutId = setTimeout(() => {
                console.log('⏱️ Request timeout - assuming OFFLINE');
                controller.abort();
            }, 8000); // Increased from 5000ms to 8000ms (8 seconds) for slow 3G tolerance

            // Try to fetch a small external resource (Google's DNS service)
            const response = await fetch('https://dns.google/resolve?name=google.com&type=A', {
                method: 'HEAD',
                mode: 'no-cors',
                cache: 'no-cache',
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            // If we succeed, reset failure counter and set online
            consecutiveFailuresRef.current = 0;
            console.log('🟢 Internet connectivity check passed - ONLINE');
            setIsOnline(true);
            checkInProgressRef.current = false;
            return true;
        } catch (error) {
            // Only set offline after multiple consecutive failures to avoid false positives
            consecutiveFailuresRef.current++;

            console.log(`🔴 Internet connectivity check failed (attempt ${consecutiveFailuresRef.current}):`, error.name, '-', error.message);

            // Increased to 4 consecutive failures for better slow connection tolerance
            if (consecutiveFailuresRef.current >= 4) {
                console.log('⚠️ Multiple consecutive failures - Setting OFFLINE');
                setIsOnline(false);
            } else {
                console.log(`ℹ️ Failure ${consecutiveFailuresRef.current}/4 detected - waiting for confirmation`);
            }

            checkInProgressRef.current = false;
            return false;
        }
    };

    useEffect(() => {
        const handleOnline = () => {
            console.log('🟢 Browser online event triggered');
            consecutiveFailuresRef.current = 0;
            setIsOnline(true);
            checkOnlineStatus();
        };

        const handleOffline = () => {
            console.log('🔴 Browser offline event triggered');
            consecutiveFailuresRef.current = 999; // High number to immediately trigger offline
            setIsOnline(false);
        };

        // Add event listeners for online/offline events
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        // Increased interval to 8 seconds to reduce aggressive checking on slow connections
        const intervalId = setInterval(() => {
            checkOnlineStatus();
        }, 8000); // Changed from 5000ms to 8000ms (8 seconds)

        // Initial check on mount
        checkOnlineStatus();

        // Cleanup event listeners and interval on unmount
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            clearInterval(intervalId);
        };
    }, []);

    return isOnline;
};